import { Component, createRef } from 'react';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';

import {
  planTableData,
} from '../../../data/pricingPlansData';
import { SILVER_PLAN, GOLD_PLAN } from '../../../data/subscriptionPlans';
import withComponentName from '../../../decorators/withComponentName';
import withPriceCurrency from '../PriceCurrencyProvider/withPriceCurrency';
import { getUserToken } from '../../../helpers/getUserToken';

import PiwikContext from '../../../services/piwikContext';

import PricingPlansTableHeader from './PricingPlansTableHeader';
import PricingPlansTableRow from './PricingPlansTableRow';
import PlanItemTableSubscribe from './PlanItemTableSubscribe';
import Preloader from '../../Preloader';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

class PricingPlansTable extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      activePlane: 'silver_plan',
      activePlaneType: '1month',
      isAddOnActive: false,
      addOnType: 'personal',
    };
    this.tableHeaderRef = createRef();
    this.scrollLeft = 0;
    this.scrollTop = 0;
    this.styleEl = null;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleWindowScrollEvent);

    const pricingTable = document.getElementById('plans_wrapper');

    pricingTable.addEventListener('scroll', () => {
      if (this.scrollLeft !== pricingTable.scrollLeft) {
        this.scrollLeft = pricingTable.scrollLeft;
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleWindowScrollEvent);

    const contactUsModal = document.querySelector('.uv-popover.uv-top.uv-scale-bottom:not(.uv-is-hidden)');
    contactUsModal?.remove();
    this.styleEl?.remove();
  }

  onSubscribe(priceInfo) {
    const { piwikEnabled, currency } = this.props;
    const { isAddOnActive, addOnType } = this.state;

    const token = getUserToken();

    if (token) {
      let {
        id,
        plan_key: planKey,
        billing_period: billingPeriod,
        billing_period_unit: billingPeriodUnit,
      } = priceInfo;
      const { gbpPlanInfo } = priceInfo;

      const addonSuffix = currency === 'gbp' ? '_gbp' : '';
      if (currency === 'gbp' && gbpPlanInfo) {
        ({
          id,
          plan_key: planKey,
          billing_period: billingPeriod,
          billing_period_unit: billingPeriodUnit,
        } = gbpPlanInfo);
      }

      if (piwikEnabled && planKey === 'silver_plan') {
        _paq.push(['trackEvent', 'Premium pricing', 'Subscribe Silver', `${billingPeriod} ${billingPeriodUnit}`]);
      } else if (piwikEnabled && planKey === 'gold_plan') {
        _paq.push(['trackEvent', 'Premium pricing', 'Subscribe Gold', `${billingPeriod} ${billingPeriodUnit}`]);
      }

      if (id) {
        let params = `purchasable_plan_id=${id}`;
        if (isAddOnActive) {
          params += `&addons=${priceInfo[`uk_addons_${addOnType}${addonSuffix}`]}`;
        }
        window.open(`/users/plan_subscription?${params}`, '_blank');
      }
    } else {
      const { setAuthModalOpened } = this.props;
      setAuthModalOpened(true);
    }
  }

  handleWindowScrollEvent() {
    const headerTag = document.getElementsByTagName('header')[0] || {};
    const topPX = (headerTag.offsetTop || 0) + (headerTag.offsetHeight || 0);
    const header = document.getElementById('pricing_plans_wrapper');
    let leftScroll;

    const storiesTop = this.tableHeaderRef.current.getBoundingClientRect().top;
    const isHeaderFixed = (storiesTop - topPX) <= 0;

    this.setState({
      isHeaderFixed,
    });

    if (window.innerWidth > 1040) {
      header.style.top = `${topPX}px`;
      return;
    }
    const wrapper = document.getElementById('plans_wrapper');
    const wrapperRect = wrapper.getBoundingClientRect();
    const pricingTable = document.getElementById('plans_wrapper');

    if (wrapperRect.height + wrapperRect.top - topPX <= 0) {
      header.style.top = '0';
    } else if (wrapperRect.top <= topPX) {
      header.style.top = `${topPX}px`;
      if (topPX) {
        header.style.position = 'fixed';
        header.style.left = `-${this.scrollLeft - 10}px`;
        // event listener to listen for vertical scroll,
        //  when header is already swiped at the start of page
        pricingTable.addEventListener('scroll', () => {
          if (leftScroll !== pricingTable.scrollLeft) {
            if (header.style.position === 'fixed') {
              header.style.left = `${-(pricingTable.scrollLeft - 10)}px`;
            }
          }
        });
      } else {
        header.style.position = 'absolute';
        header.style.left = '0';
      }
    } else {
      header.style.top = '0';
      header.style.position = 'absolute';
      header.style.left = '0';
    }
  }

  updateParentState(key, value) {
    this.setState({
      [key]: value,
    });
  }

  render() {
    const {
      activePlane,
      activePlaneType,
      isAddOnActive,
      addOnType,
      isHeaderFixed,
    } = this.state;
    const {
      userPlan,
      loading,
      premiumPlansData,
      hasInsightsAccess,
    } = this.props;
    const isUserWithPlan = [SILVER_PLAN, GOLD_PLAN].includes(userPlan);

    return (
      <div id="plans_wrapper" className={cx('plans_wrapper', { loading })}>
        {loading && <Preloader loading={loading} top="20%" />}
        <div className={cx('pricing_plans_wrapper')}>
          <div
            id="pricing_plans_wrapper"
            className={cx('plan_header_holder', { is_fixed: isHeaderFixed })}
            ref={this.tableHeaderRef}
          >
            <div id="plans" className={Styles.plans}><h2>Plans</h2></div>
            <div className={Styles.basic}><h2>Basic</h2></div>
            <div className={Styles.silver}><h2>Silver</h2></div>
            <div className={Styles.gold}><h2>Gold</h2></div>
          </div>
          <div className={Styles.pricing_plans_header}>
            {premiumPlansData.map((planItem) => (
              <PricingPlansTableHeader
                {...planItem}
                key={planItem.text}
                userPlan={userPlan}
                isUserWithPlan={isUserWithPlan}
                activePlane={!isUserWithPlan ? activePlane : ''}
              />
            ))}
          </div>
          <div className={Styles.pricing_plans_rows}>
            {planTableData.map((planItem) => (
              <PricingPlansTableRow
                {...planItem}
                key={planItem.id}
                userPlan={userPlan}
                activePlane={!isUserWithPlan ? activePlane : ''}
              />
            ))}
          </div>
          <div className={Styles.pricing_premium_wrapper} id="sub_plans_pricing">
            {premiumPlansData.map((planItem) => (
              <PiwikContext.Consumer key={planItem.text}>
                {(value) => (
                  <PlanItemTableSubscribe
                    {...planItem}
                    key={planItem.text}
                    userPlan={userPlan}
                    piwikEnabled={value}
                    isUserWithPlan={isUserWithPlan}
                    activePlane={!isUserWithPlan ? activePlane : ''}
                    activePlaneType={activePlaneType}
                    isAddOnActive={isAddOnActive}
                    addOnType={addOnType}
                    updateParentState={this.updateParentState}
                    onSubscribe={this.onSubscribe}
                    hasInsightsAccess={hasInsightsAccess}
                  />
                )}
              </PiwikContext.Consumer>
            ))}
          </div>
        </div>
        <div className={Styles.pricing_note}>
          *Earlier data is available via CSV for a fee.&nbsp;
          <a href="/contact-us">Contact us.</a>
        </div>
      </div>
    );
  }
}

export default withComponentName(withPriceCurrency(PricingPlansTable));
